/**
 * Every store read this domain makes, in one file.
 *
 * `index.ts` (imperative, `store.getState()`) and `hooks/*` (reactive, `useSelector`) are
 * two adapters over the same state. Two adapters naming reducer keys independently is the
 * drift `channel/selectors.ts` was written to prevent, so the keys live here and
 * both halves consume these functions. Plain `(state) => value`: no memoisation, nothing
 * read at module-eval time (audit checks H/I/J).
 *
 * Reducer keys used: `itemsStore`, `catalogStore`, `businessStore`, `saleDataStore`,
 * `ItemsStatusStore`. All five exist in both apps; the two FIELDS that diverge
 * (`optionSetData`'s shape, `mostSoldItems`' existence) are read through `./catalogFields` and
 * never reached for directly from here. Only `mostSoldItems` still needs a platform split there
 * — `optionSetsOf` covers both shapes in one shared function as of 2026-09-09.
 */
import { mostSoldItemIdsOf, optionSetsOf, rawMostSoldItemsOf } from "./catalogFields";
import type { ChannelRef } from "./types";

/**
 * The shared "not loaded" answers — one per selector below that can answer nothing.
 *
 * 🔴 **`?? []` / `?? {}` inline is a bug, not a style choice.** `useSelector` compares by
 * IDENTITY, so a fallback literal is a brand-new value on every call and every store notification
 * looks like a change: its component re-renders on every dispatch in the app, and a consumer that
 * derives state in an effect can loop. `ordering/cart/selectors.ts` has the full account; the
 * catalog screen's banner carousel shipped the re-render half through `kiosk/config`'s
 * `selectBanners`.
 *
 * These were latent here — both platforms' slices give every one of these fields an initial
 * value, so a booted app never reaches the fallback — and `selectors.test.ts` pins them so they
 * stay safe the day a slice stops doing that.
 *
 * ⚠️ **They are SHARED, so nothing may mutate what these selectors return on a miss.** Every
 * consumer today only reads them (`resolveItems`, `dietaryTagOf`, the schedule check), and
 * `activeItemsForChannel` deep-clones before the library writes. A writer must copy first.
 * Deliberately not frozen, matching `ordering/cart/selectors`' `NO_ITEMS` and
 * `kiosk/config/selectors`' `NO_MODE_IDS` — one convention for one hazard.
 */
const NO_ITEMS_BY_ID: Record<string, any> = {};
const NO_ITEMS: any[] = [];
const NO_CATEGORY_DATA: Record<string, any> = {};
const NO_SCHEDULES: any[] = [];
const NO_SOLD_OUT_IDS: string[] = [];
const NO_TAG_PRIORITY: (string | number)[] = [];

export function selectItemsById(state: any): Record<string, any> {
  return state?.itemsStore?.itemsData ?? NO_ITEMS_BY_ID;
}

/** The full item list, as fetched. Only `buildCatalogForChannel` needs it. */
export function selectAllItems(state: any): any[] {
  return state?.itemsStore?.allItemsList ?? NO_ITEMS;
}

/** Web stores an array, mobile a `mid`-keyed map — normalised by `./catalogFields`. */
export function selectOptionSets(state: any): any[] {
  return optionSetsOf(state?.catalogStore);
}

/**
 * The RAW field, un-normalised — for the one caller that WRITES rather than reads.
 * `prepareOptionMapForChannel` hands it to each platform's own reducer, and those expect their
 * own shape; `catalogFields` converts for readers, not for writers.
 */
export function selectRawOptionSetData(state: any): any {
  return state?.catalogStore?.optionSetData;
}

export function selectCategoryData(state: any): Record<string, any> {
  return state?.catalogStore?.categoryData ?? NO_CATEGORY_DATA;
}

export function selectItemSchedules(state: any): any[] {
  return state?.catalogStore?.scheduleData ?? NO_SCHEDULES;
}

export function selectSoldOutItemIds(state: any): string[] {
  return state?.ItemsStatusStore?.soldOutItemsList ?? NO_SOLD_OUT_IDS;
}

/**
 * "Show out-of-stock items" — the merchant setting that gates the sold-out sync.
 *
 * The same read as `helpers/catalogueHelpers`' `isDisplayOutOfStockItem()`, as a SELECTOR,
 * because this domain's rule is that every store read lives in this file. `businessStore` is
 * mounted under that name in both apps and the helper reads the identical path, so there is one
 * answer, not two.
 *
 * Consumed only by `prepareSoldOutItems` — it is a FETCH gate here, matching both of the POS's
 * ordering-flow call sites. It is deliberately not a render-time filter; `soldOut/shared.ts`
 * spells out that decision and what it costs.
 */
export function selectDisplayOutOfStockItems(state: any): boolean {
  return Boolean(state?.businessStore?.businessData?.displayItemOutOfStockStatus);
}

/**
 * Offline mode. Read here so the sold-out sync can skip a request that has no offline mirror —
 * the POS's own `getCurrentSoldOutItemsFn` opens with the same guard.
 */
export function selectIsOffline(state: any): boolean {
  return Boolean(state?.businessStore?.isOffline);
}

export function selectTagPriority(state: any): (string | number)[] {
  return state?.catalogStore?.tagsPriorityArr ?? NO_TAG_PRIORITY;
}

const NO_MENUS: { mid: string; name?: string }[] = [];

/** The business's menus (`catalogueData.menus`), each `{ mid, name }`; a shared empty when none. */
export function selectMenus(state: any): { mid: string; name?: string }[] {
  return state?.catalogStore?.catalogueData?.menus ?? NO_MENUS;
}

export function selectItemTags(state: any): any {
  return state?.catalogStore?.itemTags;
}

/**
 * `[]` on mobile: that platform has no most-sold producer at all — see `./catalogFields`.
 *
 * ⚠️ **IMPERATIVE READS ONLY.** It normalises, which means a new array per call, so a
 * `useSelector` on it is reference-unequal on every action and re-renders its component on every
 * dispatch. The reactive path takes `selectRawMostSoldItems` and normalises inside its own memo
 * — the same pairing as `selectOptionSets` / `selectRawOptionSetData`, for the same reason.
 */
export function selectMostSoldItemIds(state: any): string[] {
  return mostSoldItemIdsOf(state?.catalogStore);
}

/** The RAW field, for the reactive path. Stable while the reducer has not replaced it. */
export function selectRawMostSoldItems(state: any): any[] | undefined {
  return rawMostSoldItemsOf(state?.catalogStore);
}

/**
 * The branch the order is for: the ordering context's one read (NEW_ORDER_V2_PLAN.md §5), which is
 * the session's branch unless a chosen-branch flow (the call centre) has set one.
 */
export { selectOrderingBranchId as selectBranchId } from "../orderingContext/selectors";

export function selectBranchZoneId(state: any): string | undefined {
  return state?.businessStore?.businessData?.timeZone?.zoneId;
}

/**
 * The channel the session is ordering on. `undefined`, not `{}`, when nothing is selected —
 * the reset path clears it with `saveSelectedChannel({})`, and an object with no `channelId`
 * is not a channel. Same normalisation as `channel`'s `selectSelectedChannel`.
 */
export function selectChannel(state: any): ChannelRef | undefined {
  const channel = state?.saleDataStore?.selectedChannelData;
  return channel?.channelId ? channel : undefined;
}

/**
 * The units this merchant sells by weight or volume — `fractionAllowedUnits`.
 *
 * 🔴 **Read here, not passed in by callers.** An earlier version had each caller fetch this and
 * hand it to `addItemToCart`, which meant every POS surface had to know the store path — and two
 * of them could pass different lists, so the same item would need asking on one screen and not
 * the next. What a caller IS the authority on is whether its own customiser can take a typed
 * weight, and that is the capability it passes instead.
 *
 * ⚠️ Lives in `catalog`, not `ordering/cart`: `ordering/customiser` reads it, and `cart` depends
 * on `customiser` rather than the reverse. Putting it in `cart` inverted that.
 */
const NO_FRACTION_UNITS: string[] = [];
export function selectFractionAllowedUnits(state: any): string[] {
  const units = state?.businessStore?.businessData?.fractionAllowedUnits;
  return Array.isArray(units) ? units : NO_FRACTION_UNITS;
}

/**
 * The merchant's predefined cooking instructions (`posSettings.saleNotes`), which both POS popups
 * offer as chips. web v1 reads them through `helper/catalogueHelpers.getSaleNotes`, which v2 may not
 * import (rule 14); this reads the same field.
 */
const NO_SALE_NOTES: string[] = [];
export function selectSaleNotes(state: any): string[] {
  const notes = state?.catalogStore?.posSettings?.saleNotes;
  return Array.isArray(notes) && notes.length ? notes : NO_SALE_NOTES;
}

/** The merchant shows add-on pictures in the customiser (`posSettings.addOnsImages`, web v1's `RenderOptionSet`). */
export function selectShowsAddOnImages(state: any): boolean {
  return !!state?.catalogStore?.posSettings?.addOnsImages;
}
